/**
 * inboundAccess — geteilte Zugriffs-/Flag-Logik für das Empfangspostfach (Loop A).
 *
 * Single source of truth für die FE-Sichtbarkeit der Inbound-Funktion. Wird von
 * der Aktivierungs-Sektion (`InboundActivation`), dem Menü (`MainLayout`) und der
 * Route-Registrierung (`App.tsx`) gemeinsam genutzt — kein Copy-Paste der
 * Zugriffs-Bedingung.
 *
 * Fail-closed: Default ist KEIN Zugriff. Erst `REACT_APP_INBOUND_ENABLED='true'`
 * ODER eine Beta-Tenant-Allowlist-Mitgliedschaft (s. {@link canUseInbound})
 * schaltet die Sektion überhaupt frei — und selbst dann bleibt die Aktivierung
 * gesperrt, solange die AVV-Datei nicht (vom Operator) freigegeben ist.
 *
 * @module inboundAccess
 */

/**
 * Beta-Erprobung Empfangspostfach — entfernen/erweitern, wenn allgemein
 * freigegeben. Muster 1:1 `datevBelegeAccess.ts`/`messagingAccess.ts` (gleiche
 * Beta-Tenant-UID, gated auf `effectiveScopeUid`).
 *
 * Nur dieser Tenant-Scope (`effectiveScopeUid`) erhält Zugriff unabhängig vom
 * Env-Flag. `b3DRR1Cj63cRjdJg5VCEUpBL7hk1` = Test-Account daniel_eich@outlook.com
 * (identisch zu `MESSAGING_BETA_TENANT_UIDS`/`DATEV_BELEGE_BETA_TENANT_UIDS`).
 * HARTKODIERT für die kontrollierte Live-Erprobung; vor allgemeiner Freigabe
 * (Flag = 'true' für alle) wieder leeren.
 */
export const INBOUND_BETA_TENANT_UIDS: readonly string[] = ['b3DRR1Cj63cRjdJg5VCEUpBL7hk1'];

/**
 * Allgemeines Feature-Flag (Functions-Gegenstück: `INBOUND_ENABLED`). Fail-closed:
 * nur bei explizitem `'true'` aktiv. Modul-Konstante, einmalig beim Bundle-Load
 * ausgewertet. Solange off UND ohne Beta-Allowlist-Treffer, bleibt die Inbound-
 * Funktion für einen Tenant-Scope inert (s. {@link canUseInbound}).
 */
export const INBOUND_ENABLED_FLAG: boolean =
  process.env.REACT_APP_INBOUND_ENABLED === 'true';

/**
 * Entscheidet fail-closed, ob das Empfangspostfach (Loop A) für einen
 * Tenant-Scope sichtbar/nutzbar ist.
 *
 * WICHTIG: gated auf `effectiveScopeUid` (den Tenant-SCOPE), NICHT auf
 * `currentUser.uid` — analog `canUseMessaging`/`canUseDatevBelege`. Rein
 * kosmetisches Client-Gating; die harte Sperre bleibt die Server-seitige
 * Provisionierungs-Prüfung (`provisionInboundAddress`, `INBOUND_BETA_UIDS` in
 * `functions/src/inbound/constants.ts`).
 *
 * @param scopeUid - `effectiveScopeUid` aus `useAuth()` (oder null/undefined,
 *                   solange noch nicht aufgelöst).
 * @returns `true`, wenn `scopeUid` auf der Beta-Tenant-Allowlist steht ODER
 *          das allgemeine Flag aktiv ist. Sonst `false`.
 */
export function canUseInbound(scopeUid: string | null | undefined): boolean {
  return (!!scopeUid && INBOUND_BETA_TENANT_UIDS.includes(scopeUid)) || INBOUND_ENABLED_FLAG;
}

/**
 * Feature-Flag der manuellen Steuerkontierung (Functions-Gegenstück:
 * `INBOUND_TAX_DECISION_ENABLED`, architecture-eingang-steuerkontierung.md §9).
 * Fail-closed: nur bei explizitem `'true'` aktiv. Modul-Konstante.
 */
export const INBOUND_TAX_DECISION_ENABLED_FLAG: boolean =
  process.env.REACT_APP_INBOUND_TAX_DECISION_ENABLED === 'true';

/**
 * Entscheidet fail-closed, ob die manuelle Kontierungs-Aktion für einen
 * Tenant-Scope sichtbar/nutzbar ist. Setzt Inbound-Zugriff voraus UND das
 * eigene Flag ODER die Beta-Allowlist.
 *
 * WICHTIG (Contagion-Lehre, §5.5): das Sub-Feature reitet NICHT automatisch auf
 * einem künftigen Inbound-GA-Flip mit — es bleibt eigenständig gated. Solange
 * `INBOUND_TAX_DECISION_ENABLED_FLAG` off ist, sieht die Aktion nur die
 * Beta-Kohorte (`INBOUND_BETA_TENANT_UIDS`).
 *
 * @param scopeUid `effectiveScopeUid` aus `useAuth()` (oder null/undefined).
 * @returns `true`, wenn Inbound nutzbar UND (Flag an ODER Beta-Allowlist-Treffer).
 */
export function canUseInboundTaxDecision(
  scopeUid: string | null | undefined,
): boolean {
  return (
    canUseInbound(scopeUid) &&
    (INBOUND_TAX_DECISION_ENABLED_FLAG ||
      (!!scopeUid && INBOUND_BETA_TENANT_UIDS.includes(scopeUid)))
  );
}

/**
 * §203-Berufsgeheimnisträger-Pfad (Functions-Gegenstück: `S203_INBOUND_AVAILABLE`).
 * Fail-closed default off → Kanzlei-/StB-Accounts können das Postfach NICHT
 * aktivieren, bis die HUMAN-LAWYER-geprüfte Klausel vorliegt. Solange off, wird
 * die §203-Selbstauskunft-Frage gar nicht angezeigt (KMU-Default-Pfad).
 */
export const S203_INBOUND_AVAILABLE_FLAG: boolean =
  process.env.REACT_APP_S203_INBOUND_AVAILABLE === 'true';

/**
 * Aktuelle Inbound-AVV-Version, die der Server akzeptiert (Gegenstück zu
 * `INBOUND_AVV_VERSION` in `functions/src/inbound/constants.ts`). Muss mit dem
 * Server übereinstimmen; sonst antwortet `provisionInboundAddress` mit
 * `avv_version_stale`. Zugleich der Dateiname-Stamm des AVV-Dokuments.
 */
export const INBOUND_AVV_VERSION = 'avv-inbound-v1';

/**
 * Öffentlicher Pfad des versionierten AVV-Dokuments (statisch ausgeliefert).
 * Die Datei legt der Operator/Daniel an (J-3 Legal-Gate) — der Coder liest sie
 * nur. Fehlt sie (aktueller Zustand), lädt der Fetch 404 → fail-closed.
 */
export const INBOUND_AVV_DOC_PATH = `/legal/${INBOUND_AVV_VERSION}.md`;

/**
 * Öffentlicher Pfad des ergänzenden §203-AVV-Dokuments (Berufsgeheimnisträger,
 * `avv-inbound-s203-v1.md`, Version `inbound-s203-v1-2026-07`). Wird — analog
 * {@link INBOUND_AVV_DOC_PATH} — statisch ausgeliefert und vom Coder nur GELESEN;
 * die Datei legt der Operator/Anwalt an (J-3 Legal-Gate). `InboundActivation`
 * zeigt diesen Text am §203-Zustimmungshaken, damit der Berufsgeheimnisträger
 * die ergänzenden Bedingungen VOR der Zustimmung einsehen kann (informierte
 * Einwilligung). Fehlt die Datei (fail-closed), bleibt der §203-Haken gesperrt.
 */
export const S203_INBOUND_AVV_DOC_PATH = '/legal/avv-inbound-s203-v1.md';

/**
 * Marker, an dem der Entwurfs-Zustand des AVV-Dokuments erkannt wird. Solange die
 * Datei diesen Marker enthält (Operator setzt ihn bis zur Jurist-Freigabe), bleibt
 * die Aktivierung gesperrt.
 */
export const INBOUND_AVV_DRAFT_MARKER = 'ENTWURF';
